<template>
  <div>
    <div v-if="loading" class="text-center text-secondary py-5">
      <span class="spinner-border spinner-border-sm mr-2"></span>Loading…
    </div>

    <!-- Active matching session -->
    <div v-else-if="lines">
      <div class="bg-white border mb-3" style="border-radius:10px;padding:20px;">
        <div class="row text-center">
          <div class="col-6 col-md-3 mb-2">
            <p class="text-secondary mb-1" style="font-size:12px;">Matched</p>
            <p class="font-weight-bold mb-1">{{ lines.reconciled_count }}/{{ lines.total_count }}</p>
            <div class="progress" style="height:6px;">
              <div class="progress-bar" role="progressbar" :style="{ width: matchPercent + '%', background: '#16a34a' }"></div>
            </div>
          </div>
          <div class="col-6 col-md-3 mb-2">
            <p class="text-secondary mb-1" style="font-size:12px;">Statement closing balance</p>
            <p class="font-weight-bold mb-0">{{ formatCurrency(lines.reconciliation.closing_balance) }}</p>
          </div>
          <div class="col-6 col-md-3 mb-2">
            <p class="text-secondary mb-1" style="font-size:12px;">Dukakit balance</p>
            <p class="font-weight-bold mb-0">{{ formatCurrency(lines.dukakit_balance) }}</p>
          </div>
          <div class="col-6 col-md-3 mb-2">
            <p class="text-secondary mb-1" style="font-size:12px;">Difference</p>
            <p class="font-weight-bold mb-0" :class="difference === 0 ? 'text-dark' : 'text-danger'">
              {{ formatCurrency(difference) }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="lines.reconciliation.statement_url" class="mb-3" style="font-size:13px;">
        <i class="bi bi-file-earmark-pdf mr-1"></i>
        <a :href="lines.reconciliation.statement_url" target="_blank" rel="noopener">View bank statement</a>
        <span v-if="lines.reconciliation.statement_password" class="text-secondary ml-2">
          — password: <code>{{ lines.reconciliation.statement_password }}</code>
        </span>
      </div>

      <div v-if="error" class="alert alert-danger py-2" style="font-size:13px;">{{ error }}</div>

      <div class="table-responsive bg-white border mb-3" style="border-radius:10px;">
        <table class="table mb-0">
          <thead>
            <tr class="text-secondary" style="font-size:12px;">
              <th>Date</th>
              <th>Description</th>
              <th class="text-right">Debit</th>
              <th class="text-right">Credit</th>
              <th class="text-right">Running balance</th>
              <th class="text-center">Matched</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="lines.lines.length === 0">
              <td colspan="6" class="text-center text-secondary py-4">No transactions in this statement period.</td>
            </tr>
            <tr v-for="line in lines.lines" :key="line.id">
              <td>{{ formatDate(line.date) }}</td>
              <td class="text-dark">{{ line.description }}</td>
              <td class="text-right">{{ line.debit > 0 ? formatCurrency(line.debit) : '' }}</td>
              <td class="text-right">{{ line.credit > 0 ? formatCurrency(line.credit) : '' }}</td>
              <td class="text-right">{{ formatCurrency(line.running_balance) }}</td>
              <td class="text-center">
                <button type="button" class="btn btn-link p-0" :disabled="toggling === line.id" @click="toggleLine(line)">
                  <i
                    class="bi bi-check-circle-fill"
                    :class="{ 'text-secondary': !line.reconciled }"
                    :style="line.reconciled ? { color: '#16a34a', fontSize: '18px' } : { fontSize: '18px' }"
                  ></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="d-flex justify-content-end" style="gap:8px;">
        <button type="button" class="btn btn-light" :disabled="saving" @click="cancelReconciliation">Cancel</button>
        <button type="button" class="btn btn-primary" :disabled="saving" @click="completeReconciliation">
          <span v-if="saving"><span class="spinner-border spinner-border-sm mr-1"></span>Finishing…</span>
          <span v-else>Finish reconciling</span>
        </button>
      </div>
    </div>

    <!-- Start a new reconciliation + history -->
    <div v-else>
      <div class="bg-white border d-flex flex-wrap align-items-center mb-4" style="border-radius:10px;padding:20px;gap:20px;">
        <div class="d-flex align-items-center" style="gap:12px;flex:1;min-width:220px;">
          <i class="bi bi-arrow-left-right" style="font-size:24px;color:#0d6efd;"></i>
          <div>
            <p class="text-dark font-weight-bold mb-1">Grab your bank statement and start reconciling</p>
            <p class="text-secondary mb-0" style="font-size:12px;">Let's make sure every transaction matches. Enter the end date and closing balance, and you'll have your books up-to-date before you know it.</p>
          </div>
        </div>
        <div>
          <p class="text-secondary mb-1" style="font-size:12px;">Starting balance</p>
          <p class="font-weight-bold mb-0">{{ formatCurrency(summary ? summary.next_starting_balance : 0) }}</p>
        </div>
        <div style="min-width:160px;">
          <label class="text-secondary mb-1" style="font-size:12px;">Statement end date</label>
          <date-range-picker :auto-apply="true" :single-date-picker="true" v-model="form.datePicker" class="w-100" />
        </div>
        <div style="min-width:140px;">
          <label class="text-secondary mb-1" style="font-size:12px;">Closing balance</label>
          <input type="number" step="0.01" class="form-control" v-model.number="form.closing_balance">
        </div>
        <button type="button" class="btn btn-primary" :disabled="!canStart || saving" @click="start">
          <span v-if="saving"><span class="spinner-border spinner-border-sm mr-1"></span>Starting…</span>
          <span v-else>Start reconciling</span>
        </button>
      </div>

      <div class="mb-4" style="margin-top:-12px;">
        <a href="#" style="font-size:12px;" @click.prevent="showStatementUpload = !showStatementUpload">
          {{ showStatementUpload ? 'Hide statement attachment' : 'Attach your bank statement (optional)' }}
        </a>
        <div v-if="showStatementUpload" class="bg-white border mt-2" style="border-radius:10px;padding:16px;">
          <div class="row">
            <div class="col-md-6 mb-2">
              <label class="text-secondary mb-1" style="font-size:12px;">Statement file</label>
              <fileuploader ref="statementUploader" description="PDF or image of your bank statement, max 2MB"></fileuploader>
            </div>
            <div class="col-md-6 mb-2">
              <label class="text-secondary mb-1" style="font-size:12px;">
                Statement password <span class="text-muted">— most bank statements are password protected; saved here so you (or a teammate) can open the file later. Not used to unlock anything automatically.</span>
              </label>
              <input type="text" class="form-control" v-model="form.statement_password" placeholder="e.g. the password your bank gave you">
            </div>
          </div>
        </div>
      </div>

      <div v-if="error" class="alert alert-danger py-2" style="font-size:13px;">{{ error }}</div>

      <h6 class="text-dark mb-2" style="font-weight:600;">Reconciliation history</h6>
      <div class="table-responsive bg-white border" style="border-radius:10px;">
        <table class="table mb-0">
          <thead>
            <tr class="text-secondary" style="font-size:12px;">
              <th>Statement end date</th>
              <th>Reconciled on</th>
              <th class="text-right">Starting balance</th>
              <th class="text-right">Closing balance</th>
              <th>Statement</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!summary || summary.history.length === 0">
              <td colspan="5" class="text-center text-secondary py-4">No reconciliations yet.</td>
            </tr>
            <tr v-for="h in (summary ? summary.history : [])" :key="h.id">
              <td>{{ formatDate(h.end_date) }}</td>
              <td>{{ formatDate(h.reconciled_on) }}</td>
              <td class="text-right">{{ formatCurrency(h.starting_balance) }}</td>
              <td class="text-right">{{ formatCurrency(h.closing_balance) }}</td>
              <td>
                <a v-if="h.statement_url" :href="h.statement_url" target="_blank" rel="noopener">
                  <i class="bi bi-file-earmark-pdf mr-1"></i>View
                </a>
                <span v-else class="text-secondary">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker';

export default {
  name: 'ReconciliationPanel',

  components: { DateRangePicker },

  props: {
    accountId: { type: String, required: true },
  },

  data() {
    return {
      loading: true,
      saving: false,
      toggling: null,
      error: '',
      summary: null,
      lines: null,
      showStatementUpload: false,
      form: {
        datePicker: { startDate: null },
        closing_balance: '',
        statement_password: '',
      },
    };
  },

  computed: {
    canStart() {
      return !!this.form.datePicker.startDate && this.form.closing_balance !== '';
    },
    matchPercent() {
      if (!this.lines || this.lines.total_count === 0) return 0;
      return Math.round((this.lines.reconciled_count / this.lines.total_count) * 100);
    },
    difference() {
      if (!this.lines) return 0;
      return Math.round((this.lines.reconciliation.closing_balance - this.lines.dukakit_balance) * 100) / 100;
    },
  },

  watch: {
    accountId: {
      immediate: true,
      handler() { this.load(); },
    },
  },

  methods: {
    formatCurrency(v) {
      return 'KES ' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(d) {
      return d ? new Date(d).toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
    },

    async load() {
      this.loading = true;
      this.error = '';
      this.lines = null;
      try {
        const { data } = await axios.get(`/api/ledger/accounts/${this.accountId}/reconciliation`);
        this.summary = data;
        if (data.in_progress) {
          await this.loadLines(data.in_progress.id);
        }
      } catch (e) {
        this.error = e.response?.data?.message || 'Could not load reconciliation.';
      } finally {
        this.loading = false;
      }
    },

    async loadLines(reconciliationId) {
      const { data } = await axios.get(`/api/ledger/reconciliation/${reconciliationId}`);
      this.lines = data;
    },

    async start() {
      this.saving = true;
      this.error = '';
      try {
        const uploader = this.showStatementUpload ? this.$refs.statementUploader : null;
        const { data } = await axios.post(`/api/ledger/accounts/${this.accountId}/reconciliation`, {
          end_date: moment(this.form.datePicker.startDate).format('YYYY-MM-DD'),
          closing_balance: this.form.closing_balance,
          statement_file: uploader ? uploader.fileuploaded : '',
          statement_password: this.form.statement_password,
        });
        this.lines = data;
      } catch (e) {
        this.error = e.response?.data?.message || 'Could not start reconciliation.';
      } finally {
        this.saving = false;
      }
    },

    async toggleLine(line) {
      this.toggling = line.id;
      try {
        await axios.post(`/api/ledger/reconciliation/lines/${line.id}/toggle`);
        await this.loadLines(this.lines.reconciliation.id);
      } catch (_) {
      } finally {
        this.toggling = null;
      }
    },

    async completeReconciliation() {
      this.saving = true;
      try {
        await axios.post(`/api/ledger/reconciliation/${this.lines.reconciliation.id}/complete`);
        this.$toasts.push({ type: 'success', message: 'Reconciliation completed.' });
        this.$emit('changed');
        await this.load();
      } catch (e) {
        this.error = e.response?.data?.message || 'Could not complete reconciliation.';
      } finally {
        this.saving = false;
      }
    },

    async cancelReconciliation() {
      if (!confirm('Cancel this reconciliation? Matched transactions will keep their status, but this session will be discarded.')) return;
      this.saving = true;
      try {
        await axios.delete(`/api/ledger/reconciliation/${this.lines.reconciliation.id}`);
        this.$emit('changed');
        await this.load();
      } catch (_) {
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
